import { kitText } from './messages.ts';

/**
 * §3.7 — what "not ready yet" looks like.
 *
 * An account exists the moment it is created and is unusable until two more
 * things happen. Showing only "Active" would have an administrator tell somebody
 * their access is set up when it is not, and the person is then locked out of a
 * console with nobody to ask.
 */
export function readiness(
  row: {
    status: string;
    mfa_enrolled: boolean;
    must_change_password: boolean;
  },
  /*
   * Whether this deployment is asking for the second factor at all.
   *
   * `DISABLE_MFA_FOR_TESTING` turns the check off for a whole process, and
   * staging runs that way. The sentence below is the one an administrator
   * acts on, so it has to be true of the deployment in front of them: saying
   * somebody cannot sign in, on a server that will let them straight in,
   * sends that administrator hunting for a lockout that is not there.
   */
  mfaEnforced = true,
): { label: string; tone: 'locked' | 'provisional' | 'overdue'; why?: string } {
  if (row.status !== 'ACTIVE') return { label: row.status, tone: 'overdue' };
  /*
   * A missing authenticator is a step still outstanding only where this
   * deployment asks for one.
   *
   * It used to read "Awaiting setup" whenever `mfa_enrolled` was false, and
   * then explain — in the same breath — that the deployment is not asking for
   * an authenticator so they can sign in without it. So a person who had
   * accepted their invitation, changed their password and been working for
   * days still showed as waiting on something, and the sentence beside the
   * chip said there was nothing to wait for. Reported 2026-09-29.
   *
   * Where MFA is not enforced, enrolment is not a state of this account at
   * all, and the account falls through to the password check and to Active.
   */
  if (!row.mfa_enrolled && mfaEnforced) {
    return {
      label: kitText('staff.state.awaiting'),
      tone: 'provisional',
      why: kitText('staff.state.awaiting.why'),
    };
  }
  if (row.must_change_password) {
    return {
      label: kitText('staff.state.temporary'),
      tone: 'provisional',
      why: kitText('staff.state.temporary.why'),
    };
  }
  return { label: kitText('staff.state.active'), tone: 'locked' };
}
